import { useMemo } from 'react';
import { Outlet, useLocation } from 'react-router';

import { BannerAds } from '#/components/BannerAds/BannerAds';
import { PageFooterLoadable } from '#/components/PageFooter/PageFooterLoadable';
import { RouteLoadingProgressBar } from '#/components/RouteLoadingProgressBar/RouteLoadingProgressBar';
import { AcceptCookiesContainer } from '#/containers/AcceptCookiesContainer';
import { InviteAcceptedToastContainer } from '#/containers/InviteAcceptedToast/InviteAcceptedToastContainer';
import { SignInOrUpReminderContainer } from '#/containers/SignInOrUpReminder/SignInOrUpReminderContainer';
import { useUser } from '#/contexts/UserEnvironmentContext';
import { useAdManager } from '#/hooks/useAdManager';
import { useCombinedRouterMatchesHandle } from '#/hooks/useCombinedRouterMatchesHandle';

import { LayoutHeader } from './Header/LayoutHeader';
import {
  LayoutBaseVariantProgressBarHolder,
  LayoutChildrenHolder,
  LayoutHolder,
  LayoutMobileSearchPageFooterPadding,
} from './Layout.styled';
import { LayoutBottomOfPageElement } from './LayoutBottomOfPageElement';
import type { LayoutHandleProps } from './LayoutHandleProps';

export function Layout() {
  const location = useLocation();

  const {
    noAds: noAdsHandle,
    darkBackground,
    hideFooterDivider: hideFooterDividerHandle,
    defaultSearchType = 'JOB',
    searchFromPageType,
    showDesktopSearch = false,
    isSearchPage = false,
    isSearchLandingPage = false,
    showSearchRefinements = false,
    variant = 'default',
    urlByLocale,
    fallbackUrlByLocaleToCurrent,
  } = useCombinedRouterMatchesHandle<LayoutHandleProps>();

  const { user } = useUser();
  const isLoggedOut = !user;

  const noAds = useMemo(() => {
    if (noAdsHandle === 'no-ads-if-logged-out') return isLoggedOut;

    return noAdsHandle;
  }, [noAdsHandle, isLoggedOut]);

  const hideFooterDivider = useMemo(() => {
    if (hideFooterDividerHandle === 'hide-footer-divider-if-logged-out')
      return isLoggedOut;

    return hideFooterDividerHandle;
  }, [hideFooterDividerHandle, isLoggedOut]);

  useAdManager(noAds);

  return (
    <LayoutHolder data-dark-background={darkBackground ? true : undefined}>
      {variant !== 'base' && <BannerAds />}

      {variant === 'base' ? (
        <LayoutBaseVariantProgressBarHolder>
          <RouteLoadingProgressBar />
        </LayoutBaseVariantProgressBarHolder>
      ) : (
        <LayoutHeader
          isSearchPage={isSearchPage}
          isSearchLandingPage={isSearchLandingPage}
          defaultSearchType={defaultSearchType}
          searchFromPageType={searchFromPageType}
          showDesktopSearch={showDesktopSearch}
          showSearchRefinements={showSearchRefinements}
        />
      )}

      <LayoutChildrenHolder data-qa-id="layout-content">
        <Outlet />
      </LayoutChildrenHolder>

      {variant !== 'base' && (
        <>
          <LayoutBottomOfPageElement />

          <PageFooterLoadable
            urlByLocale={
              urlByLocale ??
              (fallbackUrlByLocaleToCurrent
                ? {
                    en: location.pathname,
                    es: location.pathname,
                    pt: location.pathname,
                  }
                : undefined)
            }
            showDivider={!hideFooterDivider}
          />

          {isSearchPage && <LayoutMobileSearchPageFooterPadding />}
        </>
      )}

      <AcceptCookiesContainer />
      <SignInOrUpReminderContainer />
      <InviteAcceptedToastContainer />
    </LayoutHolder>
  );
}

export default Layout;
